import { Schema } from "effect"
import { AllowEntry, AllowRule } from "../allow"
import {
  EntityRef,
  EventRef,
  Evidence,
  Health,
  Hold,
  Principal,
  Reversibility,
  Risk,
  Taint,
  Visibility,
} from "../common"
import { WorkAssignment, WorkReply, WorkRequest, WorkRequestDraft, WorkVerification } from "../coordination"
import { DocMeta, LoadedDocument } from "../documents"
import { Monitor, WakeReason } from "../domain/agentlife"
import { BriefSections } from "../domain/brief-sections"
import { ArtifactKind } from "../domain/item"
import { MemoryDraft, MemoryScope } from "../domain/knowledge"
import { Capability, Mind, PolicyDocument } from "../domain/org"
import { BetVerdictValue, KpiEvidence, KpiSample } from "../domain/plan"
import {
  CARD_ASK_MAX,
  CardRecommendation,
  PlanQuestion,
  ProposalBody,
  ProposalState,
  QuestionOption,
} from "../domain/proposal"
import { MessageParty, MessageRoute, ParticipantId } from "../domain/thread"
import { Cadence, Member, RepoBaseBranch, WorkerSettings } from "../domain/workspace"
import { MergeReport, PrSnapshot } from "../github"
import {
  AgentId,
  AreaId,
  ArtifactId,
  ChangesetId,
  CommandId,
  CommentId,
  Epoch,
  Handle,
  IntegrationId,
  ItemId,
  KpiId,
  Markdown,
  MemoryItemId,
  Millis,
  MonitorId,
  NonNegInt,
  PolicyId,
  PosInt,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  RunnerId,
  Seq,
  Sha256,
  Slug,
  UserId,
  WakeId,
  WorkRequestId,
  WorkspaceId,
} from "../ids"
import { LendCandidate, Unmet } from "../placement"
import { RunResources } from "../resources"
import { RoutingPolicy } from "../routing"
import {
  Executor,
  RateLimitReset,
  RunError,
  RunHead,
  RunKind,
  RunOutcome,
  RunPlace,
  SessionRef,
  StopReason,
  TurnOrigin,
  Usage,
  Verdict,
  WorkScope,
} from "../run"
import { ComputeUsage, FundedBy } from "../usage"
import { FlowCommand, GraphCommand, Wip } from "./graph"

// #region envelope
const opt = Schema.optionalKey
/** Decision 0150: why a run's pre-start lease ended (the hub's release reason), recorded on its `RunRequeued`. */
const PrestartRequeueReason = Schema.Literals(["ceiling", "prepare_failed", "shutdown", "expired"])

// LogId and EventRef live in `common` (Wave A: the chain card's `held` entries need them in `domain/proposal`).
export { EventRef, LogId } from "../common"

/** What every writer sends. There is no `actor` field: the server stamps it from the authenticated credential. */
export const CommandEnvelope = Schema.Struct({
  commandId: CommandId, // client-minted; the default idempotency key
  /** Stable key when one logical intent may be re-issued with new commandIds (agent retries). */
  idempotencyKey: opt(Schema.NonEmptyString),
  /** Last seq the issuer had applied. A rebase hint for PreconditionFailed, never a blanket rejection. */
  baseSeq: Seq,
  /** Optimistic preconditions on exactly the records this command depends on. */
  expect: opt(Schema.Array(Schema.Struct({ ref: EntityRef, lastSeq: Seq }))),
  /** Direct cause: a command, or an event in any log (e.g. the wake that started this turn). */
  causedBy: opt(Schema.Union([CommandId, EventRef])),
  /** Further causes, e.g. the ten coalesced item completions behind one lead turn. */
  links: opt(Schema.Array(EventRef)),
  /** Inherited; minted only at true roots (human input, schedule tick, webhook). Also seeds the OTel trace id. */
  correlationId: opt(Schema.String),
  command: Schema.suspend(() => Command),
})
export type CommandEnvelope = typeof CommandEnvelope.Type
// #endregion envelope

// #region commands
/** Needs-you actions on one proposal: Approve · Edit (approve_edited) · Reply (answer) · Reject; Defer is client-side. */
export const ProposalDecision = Schema.TaggedUnion({
  approve: {
    selection: opt(Schema.Array(Schema.Int)), // plan_change: accept per change (dependency-closed)
    /** Permission proposals only (settings wave §4.5): "run" = allow this tool for the rest of the run. "always"
     *  (decision 0053) = permission proposals whose body offers `options.alwaysPrefix`: Codex's own allow rule. */
    remember: opt(Schema.Literals(["once", "run", "always"])),
    /**
     * Wave O1 (decision 0061): answers to a `plan_change` card's `questions`, by key. A question left out takes Lea's
     * recommendation, so a plain Approve means "use your recommendations".
     */
    answers: opt(
      Schema.Array(
        Schema.Struct({
          key: Slug,
          text: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(1_000)),
        }),
      ).check(Schema.isMaxLength(8)),
    ),
  },
  /** People only: approve a changed body of the same kind ("remember that…": confirm or fix scope and type). Written
   *  as ProposalRevised (by the approver) + ProposalApproved at the new revision, in one transaction. */
  approve_edited: { body: ProposalBody },
  reject: { reason: opt(Schema.String) },
  request_revision: { note: Schema.NonEmptyString },
  answer: { optionKey: Schema.NullOr(Schema.String), text: Schema.NullOr(Schema.String) }, // question
  adopt: {}, // lead adopts a Mayor suggestion: re-filed as the lead's own SubmitChangeset under its policy
})
export type ProposalDecision = typeof ProposalDecision.Type

/** Project DO commands that are not graph edits. Graph edits only travel inside SubmitChangeset. */
export const ProjectCommand = Schema.TaggedUnion({
  RequestProjectWork: { draft: WorkRequestDraft, proposalId: ProposalId },
  CancelProjectWork: { requestId: WorkRequestId, revision: PosInt, note: Schema.NonEmptyString },
  LinkProjectWork: { requestId: WorkRequestId, revision: PosInt, itemId: ItemId },
  ReceiveWorkControl: {
    requestId: WorkRequestId,
    revision: PosInt,
    note: Schema.String,
    assignment: Schema.optionalKey(WorkAssignment),
  },
  ReceiveProjectWork: { request: WorkRequest },
  ReplyProjectWork: { requestId: WorkRequestId, reply: WorkReply },
  VerifyProjectWork: { requestId: WorkRequestId, verification: WorkVerification },
  ReceiveProjectReply: { requestId: WorkRequestId, reply: WorkReply },
  RecordWorkDelivery: {
    requestId: WorkRequestId,
    revision: PosInt,
    replyId: Schema.optionalKey(CommandId),
    control: Schema.optionalKey(Schema.Boolean),
    problem: Schema.NullOr(Schema.String),
  },
  // ---- plan (bumps the plan version) ----
  /** apply_allowed (default): apply the dependency-closed allowed subset now, propose the rest as ONE proposal.
   *  propose_all: any "propose" verdict turns the whole set into one proposal (atomic review). */
  SubmitChangeset: {
    changesetId: ChangesetId,
    rationale: Markdown,
    changes: Schema.NonEmptyArray(GraphCommand),
    onPropose: Schema.Literals(["apply_allowed", "propose_all"]),
    /**
     * Wave O1 (decision 0061, decide first): questions only the person can answer, and the item whose description
     * receives the answers. They go on the plan_change card if any part of the changeset becomes one; an applied
     * changeset drops them. Optional keys: stored envelopes decode and hash as before.
     */
    questions: opt(Schema.Array(PlanQuestion).check(Schema.isMaxLength(8))),
    containerId: opt(ItemId),
    /**
     * Decision 0134 (lesson 1): a person's import of history (`plan {import: true}` over MCP). Its changeset is marked,
     * and nothing in it wakes an agent; the onboarding hand-off (`ask_lead`) is the one wake. People only.
     */
    import: opt(Schema.Literal(true)),
  },
  UndoChangeset: { changesetId: ChangesetId, target: ChangesetId }, // forward-only inverse
  RevertPlan: { changesetId: ChangesetId, toVersion: Schema.Int, rationale: Markdown }, // forward-only diff back to v
  // ---- ledger ----
  OpenProposal: {
    proposalId: ProposalId,
    body: ProposalBody,
    reasoning: Markdown,
    oneLine: Schema.String,
    risk: opt(Risk),
    reversibility: opt(Reversibility),
    blocks: Schema.Array(EntityRef),
    decideBy: opt(Millis),
    intentKey: Schema.String,
    /**
     * Questions and plan cards (an agent's `ask_human{to}`, `propose_plan{to}`; spec §2.4, decision 0127): the member
     * the card is for. Omitted: the person responsible for the subject, else the person who asked, else the project's
     * admins. Additive: v1 rows never carry it.
     */
    to: opt(UserId),
    /** Decision 0144: the question a person answers, and the proposer's recommended answer (see `Proposal`). */
    ask: opt(Schema.String.check(Schema.isMaxLength(CARD_ASK_MAX))),
    recommendation: opt(CardRecommendation),
  },
  ReviseProposal: { proposalId: ProposalId, revision: Schema.Int, body: ProposalBody, reasoning: Markdown },
  WithdrawProposal: { proposalId: ProposalId },
  DecideProposal: {
    proposalId: ProposalId,
    revision: Schema.Int, // must equal the current revision
    contentHash: Sha256, // must equal the current hash: you approve exactly what you saw
    decision: ProposalDecision,
    /**
     * Decision 0093: an agent applies the answer the person gave in its chat (`resolve_card`). Only in a turn that person
     * started; decide records the person as the decider and the agent as `relayedBy`. `quote` is the person's words.
     */
    relay: opt(
      Schema.Struct({
        quote: Schema.NonEmptyString.check(Schema.isMaxLength(500)),
        /**
         * Decision 0142 (0140 #22): the person's words are in a teammate agent's team chat, not in this turn. The tool
         * layer found `quote` in that person's message there; decide applies it as them from any turn of a project
         * agent, and the decision records the chat message as its evidence.
         */
        from: opt(Schema.Struct({ agentId: AgentId, messageId: Schema.String, userId: UserId })),
      }),
    ),
    /**
     * Decision 0102: an agent deciding a card addressed to it says why (`decide_card`); recorded on the decision (the
     * resolution's note, a reject's reason). Ignored for people.
     */
    note: opt(Schema.String.check(Schema.isMaxLength(2_000))),
  },
  /**
   * Decision 0102: the agent a card is addressed to passes it one level up with its recommendation (`escalate_card`):
   * an area lead to the project lead, the project lead to the person.
   */
  EscalateProposal: {
    proposalId: ProposalId,
    revision: Schema.Int,
    note: Schema.NonEmptyString.check(Schema.isMaxLength(2_000)),
  },
  ExpireProposal: { proposalId: ProposalId },
  // ---- steering (people; owner/admin-gated; not plan edits) ----
  SetHold: { ref: EntityRef, hold: Hold },
  UpdateProject: {
    patch: Schema.Struct({
      name: opt(Schema.String),
      /**
       * Deprecated (decision 0049): decide refuses it; the brief changes only through ReviseBrief. Kept so stored v1
       * command envelopes and stale clients decode and get a clear Invalid, never a silent drop.
       */
      description: opt(Markdown),
      status: opt(Schema.Literals(["active", "paused", "archived"])),
      repos: opt(Schema.Array(RepoId)),
      wip: opt(Wip),
      /** Settings wave: the project's worker presets (saved whole). */
      workers: opt(WorkerSettings),
      routing: opt(RoutingPolicy),
      /** Settings wave: per-repo base branches (the registry default = no entry). */
      baseBranches: opt(Schema.Array(RepoBaseBranch)),
      /**
       * Wave C (decision 0071): the time zone and the daily check's hour, saved whole (ProjectUpdated merges shallowly).
       * Project managers; decide checks the zone (`isTimeZone`).
       */
      cadence: opt(Cadence),
      /** Decision 0096: the project's sandbox allowlist, saved whole. Project managers. */
      allow: opt(Schema.Array(AllowEntry)),
      /**
       * Decision 0129: `private` (its members only, and workspace owners/admins) or `workspace` (every workspace member
       * sees it and may join). Project admins.
       */
      visibility: opt(Visibility),
    }),
  },
  CreatePolicy: {
    policyId: PolicyId,
    subject: Schema.TaggedUnion({ project: {}, area: { areaId: AreaId }, agent: { agentId: AgentId } }),
    document: PolicyDocument,
  },
  RevisePolicy: {
    policyId: PolicyId,
    revision: Schema.Int,
    document: PolicyDocument,
    restoredFrom: opt(Schema.Int),
  },
  /**
   * system:upgrade only (decision 0032). For every policy that mentions `like` and not `add`, copy `like`'s rules to
   * `add`. Kept decodable for stored v1 commands and events; the v2 Project DO never issues it (the backfill is a
   * read-time rule since decision 0036).
   */
  UpgradePolicies: { add: Capability, like: Capability },
  /**
   * system:upgrade only (decision 0134 addendum): every agent policy that still equals an earlier `lead` or `role`
   * preset exactly gets that preset's 0134 change (one PolicyRevised each). Issued once per project by the Project DO.
   */
  UpgradePolicyPresets: { decision: Schema.Literal("0134") },
  GrantIntegration: {
    areaId: AreaId,
    grant: Schema.Struct({
      integrationId: IntegrationId,
      read: Schema.Array(Schema.String),
      write: Schema.Array(Schema.String),
      outward: Schema.Array(Schema.String),
    }),
  },
  RevokeIntegration: { areaId: AreaId, integrationId: IntegrationId },
  // ---- runs ----
  /** Issued by the DO's effect layer after assembling the context pack (scheduler reaction, review stage, retry,
   *  approved publish, or an Agent DO turn request). decide validates and dedupes on the run's idempotency key. */
  RequestRun: {
    runId: RunId,
    kind: RunKind,
    itemId: opt(ItemId),
    subjectRunId: opt(RunId),
    publish: opt(Schema.Struct({ proposalId: ProposalId, revision: Schema.Int })),
    agentId: opt(AgentId),
    wakeBatchId: opt(WakeId),
    context: Schema.Struct({
      hash: Sha256,
      manifestRef: Schema.String,
      taint: Taint,
      documents: opt(Schema.Array(LoadedDocument)),
    }),
    executor: opt(Executor), // platform turns and gateway publish runs; runner runs get theirs at lease time
    /** Workers and validators: the job's worktree branch (null = scratch). Display only; copied to RunRequested. */
    branch: opt(Schema.NullOr(Schema.String)),
    /** Turns only (decision 0048); the Project DO sets it from the wake batch (core `isPersonStarted`). */
    startedBy: opt(TurnOrigin),
    /** Turns only (Wave A); the Project DO sets it from the wake batch (core `batchChain`). Absent = 0. */
    chain: opt(NonNegInt),
    /**
     * Turns only (wave F, decision 0062): who asked; the Project DO sets it from the wake batch (core `askedByOfBatch`).
     * Attribution only: never a reason to reject. Absent = the agent on her own initiative. Workers, validators and
     * publish runs get theirs from the item or proposal in decide.
     */
    askedBy: opt(Principal),
    wakeReasons: opt(Schema.Array(WakeReason).check(Schema.isMaxLength(10))),
    workScope: opt(WorkScope),
  },
  CancelRun: { runId: RunId, reason: Schema.String },
  /**
   * Wave E (decision 0063, §15 decision 2(a) "Run here, per item"): a person lends their own personal runner to one
   * item (its workers, validators and retries) when only that runner can do it. `runnerId: null` ends the lend. Decide
   * emits `ItemUpdated{ patch: { lentRunnerId } }`; any shaping command ends it.
   */
  LendRunner: { itemId: ItemId, runnerId: Schema.NullOr(RunnerId) },
  /**
   * Decision 0138: a runner owner's grants changed (Settings › Runners, or "Always allow" on a card from their machine).
   * Sent by the Workspace DO as that owner to each project with a run on the runner; decide answers Allow once every
   * open permission card from that machine (theirs to decide) that the rules now cover, noted "covered by …".
   */
  ApplyRunnerGrants: { runnerId: RunnerId, rules: Schema.Array(AllowRule).check(Schema.isMaxLength(100)) },
  /** Hub; only when the reasons change. `lendable` (wave E): runners whose owners could lend them to the item. */
  RecordRunWaiting: {
    runId: RunId,
    unmet: Schema.Array(Unmet),
    since: Millis,
    lendable: opt(Schema.Array(LendCandidate)),
    /** Decision 0150: why the run's last pre-start lease ended, when this follows one (else `expired` is recorded). */
    requeueReason: opt(PrestartRequeueReason),
  },
  /**
   * Re-walk 2026-10-28 (decision 0166 §2): the scheduler's probe of a queued message's turn (it waits behind another
   * turn): what that turn would wait on with `askerId` as its asker. Opens the share cards for it now. `unmet: []`
   * (it would run) clears an earlier one.
   */
  RecordAskWaiting: {
    agentId: AgentId,
    askerId: UserId,
    unmet: Schema.Array(Unmet),
    since: Millis,
    lendable: opt(Schema.Array(LendCandidate)),
  },
  /**
   * The hub took a lease back from a runner that can't run it (decision 0039: a runner without the `item_model` feature,
   * at Register or Renew) and queued the run again. The attempt is not counted again: the item keeps its live run.
   */
  RecordRunRequeued: { runId: RunId, epoch: Epoch, reason: Schema.Literal("runner_outdated") },
  // hub (phase: preparing); decision 0150: `requeueReason` = why the previous pre-start lease ended
  RecordRunLeased: {
    runId: RunId,
    epoch: Epoch,
    executor: Executor,
    requeueReason: opt(PrestartRequeueReason),
  },
  RecordRunStarted: { runId: RunId, epoch: Epoch, sessionIn: Schema.NullOr(SessionRef) }, // hub / Agent DO (phase: running)
  RecordRunMilestone: {
    runId: RunId,
    epoch: Epoch,
    kind: Schema.Literals([
      "pr_opened",
      "tests_passed",
      "tests_failed",
      "checkpoint",
      "awaiting_permission",
      "note",
    ]),
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  /**
   * Decision 0059: the PR watch (runner, via the hub) or the hub's merge expiry (system:scheduler). Never creates
   * anything. A system actor sends only `snapshot: null` with an `expired` merge; a runner never sends `expired`.
   * `runnerName` is stamped by the hub (the reporting runner, or the one an expired order was sent to) for the merge
   * sentences ("gh is not logged in on <runner>"); absent, they say "the runner".
   */
  RecordPrState: {
    artifactId: ArtifactId,
    snapshot: Schema.NullOr(PrSnapshot),
    merge: opt(MergeReport),
    runnerName: opt(Schema.String.check(Schema.isMaxLength(200))),
  },
  ReportOutcome: { outcome: RunOutcome }, // MCP report_outcome; run and epoch come from the token
  ReportVerdict: { verdict: Verdict }, // MCP report_verdict (validators)
  RaiseRunTaint: { runId: RunId, epoch: Epoch, source: Schema.String }, // MCP endpoint / gateway saw an untrusted read
  RecordRunEnded: {
    runId: RunId,
    epoch: Epoch,
    stopReason: StopReason,
    usage: Schema.Array(Usage),
    error: Schema.NullOr(RunError),
    sessionOut: Schema.NullOr(SessionRef),
    transcriptRef: Schema.NullOr(Schema.String),
    /** Decision 0059: the worktree's head (worker runs on runner ≥ 0.7.0). */
    head: opt(RunHead),
    /**
     * Wave F (decision 0062): whose plan or key paid. Runner: kind plan | key | unknown with its own runnerId (the hub
     * fills ownerId from its RunnerRecord); agent (platform turns): workspace | platform; system: absent. An invalid
     * value is dropped by decide, never a reason to refuse the end.
     */
    fundedBy: opt(FundedBy),
    /** Wave F: sandbox compute rows (later; platform runners only). */
    compute: opt(Schema.Array(ComputeUsage)),
    /** Decision 0095: where it ran, from the hub (runner record and lease); never from the runner. */
    place: opt(RunPlace),
    /** Decision 0149: what the run used (the runner's sampler, sanitized by the hub). Ids and numbers only. */
    resources: opt(RunResources),
    /** Decision 0155: a `rate_limited` run's known reset, from the hub (the provider's words or the profile's quota). */
    rateLimit: opt(RateLimitReset),
  },
  // ---- review ----
  RecordReview: {
    itemId: ItemId,
    round: Schema.Int,
    verdict: Schema.Literals(["approve", "changes_requested"]),
    notes: Markdown,
    findings: Schema.Array(Schema.String),
  },
  // ---- comments and the agent bus ----
  /** With `health`: an `update` comment on a goal or an item (its health until the next update). */
  PostComment: {
    commentId: CommentId,
    target: EntityRef,
    threadId: Schema.NullOr(CommentId),
    body: Markdown,
    health: opt(Health),
    /** `true`: a `decision` comment on an item or a goal (not with `health`). Decision 0049. */
    decision: opt(Schema.Boolean),
  },
  EditComment: { commentId: CommentId, body: Markdown },
  ResolveThread: { threadId: CommentId },
  /**
   * `to` names the agent asked (any agent in the project but the asker; Wave A). Without it: a worker asks its item's
   * owner agent, a role agent the lead (decide resolves it).
   */
  Ask: { commentId: CommentId, target: EntityRef, body: Markdown, to: opt(AgentId) },
  Answer: { commentId: CommentId, askId: CommentId, body: Markdown },
  /**
   * Decision 0142: a message in a thread (`T-n`). `thread` continues one (its id); without it, decide continues the
   * thread `on` that record (or the direct thread) with exactly these parties, else opens a new `T-n`. `to` empty: the thread's other parties. Agents, workers and people send; a person's message
   * through one of their external participants names it in `via` (the tool layer checked it is the caller's).
   */
  SendMessage: {
    commentId: CommentId,
    thread: Schema.NullOr(CommentId),
    on: Schema.NullOr(EntityRef),
    to: Schema.Array(MessageParty).check(Schema.isMaxLength(20)),
    body: Markdown,
    expectsReply: opt(Schema.Boolean),
    via: opt(MessageParty),
  },
  /**
   * Decision 0142: a person's external participant (their dot, their coding session) took these messages (with a tool
   * call, `read_messages` or a poll). Records `MessageDelivered{state: read}` once per message.
   */
  MarkMessagesRead: {
    participantId: ParticipantId,
    commentIds: Schema.Array(CommentId).check(Schema.isMaxLength(200)),
  },
  /**
   * Decision 0142, system:gateway: a delivery that happened outside a message commit, for the log: the lead's chat reply
   * to a person's `ask_lead` message, pushed to the client that asked (`message.received`). `excerpt` is what was sent.
   */
  RecordMessageDelivery: {
    chat: Schema.Struct({ agentId: AgentId, messageId: Schema.String, replyTo: Schema.String }),
    to: MessageParty,
    route: MessageRoute,
    excerpt: Schema.String.check(Schema.isMaxLength(4_000)),
  },
  // ---- artifacts ----
  PostArtifact: {
    artifactId: ArtifactId,
    itemId: Schema.NullOr(ItemId),
    kind: ArtifactKind,
    title: Schema.String,
    uri: Schema.String,
    contentType: Schema.NullOr(Schema.String),
    sizeBytes: Schema.NullOr(Schema.Int),
    sha256: Schema.NullOr(Sha256),
    preview: Schema.NullOr(Schema.String),
    interfaceKey: Schema.NullOr(Slug),
    supersedes: Schema.NullOr(ArtifactId),
    /** Decision 0050: a file from outside the project; it can only raise taint. Absent on old commands. */
    untrusted: opt(Schema.Boolean),
    /**
     * Wave DOC (decision 0068): a project document (people only, `itemId: null`). Absent on outputs and on old
     * commands; a version without it of a document carries the old version's `doc`.
     */
    doc: opt(DocMeta),
  },
  SetArtifactState: {
    artifactId: ArtifactId,
    state: Schema.Literals(["draft", "final", "published", "rejected"]),
  },
  // ---- memory (project, area, item scopes here; others applied cross-DO) ----
  ProposeMemory: {
    proposalId: ProposalId,
    /** Decision 0146: `confirm` stamps `lastConfirmedAt` on an accepted item (never a card, no new revision). */
    op: Schema.Literals(["create", "revise", "archive", "promote", "confirm"]),
    itemId: MemoryItemId,
    draft: Schema.NullOr(MemoryDraft),
    fromScope: Schema.NullOr(MemoryScope),
    evidence: Schema.Array(Evidence),
    reasoning: Markdown,
    /** Decision 0146: a create that supersedes this claim (kept, marked superseded with `validTo`). */
    supersedes: Schema.optionalKey(MemoryItemId),
  },
  WriteMemory: {
    itemId: MemoryItemId,
    op: Schema.Literals(["create", "revise", "archive", "revoke"]),
    draft: Schema.NullOr(MemoryDraft),
    reason: Schema.NullOr(Schema.String),
  }, // people only
  // ---- outward actions (Phase 2) ----
  RequestOutwardAction: {
    proposalId: ProposalId,
    itemId: ItemId,
    integrationId: IntegrationId,
    tool: Schema.String,
    args: Schema.Json,
    preview: Schema.Struct({
      summary: Schema.String,
      recipient: Schema.NullOr(Schema.String),
      amount: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Markdown),
      diffArtifactId: Schema.NullOr(ArtifactId),
    }),
  },
  RecordOutwardResult: {
    proposalId: ProposalId,
    revision: Schema.Int,
    runId: RunId,
    result: Schema.TaggedUnion({ ok: { resultRef: Schema.String }, failed: { error: Schema.String } }),
  },
  // ---- KPIs and outcomes ----
  /** Decision 0045: agents and workers too, with evidence (≥ 1, checked by decide); a person's needs none. */
  RecordKpiSample: {
    kpiId: KpiId,
    sample: KpiSample,
    evidence: Schema.optionalKey(Schema.Array(KpiEvidence).check(Schema.isMaxLength(20))),
  },
  /**
   * Decision 0097: many samples at once (a backfill, or several KPIs from one fetch), each checked as RecordKpiSample;
   * all or none apply.
   */
  RecordKpiSamples: {
    samples: Schema.Array(
      Schema.Struct({
        kpiId: KpiId,
        sample: KpiSample,
        evidence: Schema.optionalKey(Schema.Array(KpiEvidence).check(Schema.isMaxLength(20))),
      }),
    ).check(Schema.isMinLength(1), Schema.isMaxLength(500)),
  },
  /**
   * Decision 0098: an agent's brief to a person (what happened, what's next, what it needs from them, by ref). Not
   * the project brief (ReviseBrief, decision 0049). Decision 0130 (0126 §9): briefs are per person; `to` is a working
   * member of the project, default the agent's keeper. Lands in that person's inbox; Home shows each person the
   * lead's newest brief to them.
   */
  /** Decision 0144: `sections` (the fixed sections plus free text); `body` may then be empty (decide renders it). */
  SendBrief: { body: Markdown, to: opt(UserId), sections: opt(BriefSections) },
  RecordBetVerdict: {
    itemId: ItemId,
    verdict: BetVerdictValue,
    evidence: Schema.Array(Evidence),
  }, // Phase 3, evaluator agent
  // ---- the overdue alarm (system:scheduler; G6) ----
  /** A person's item passed its due date while it holds others: ItemOverdue, once per due date. */
  NoteOverdue: { itemId: ItemId },
  /**
   * Decision 0067 (system:scheduler): after a hydration put back the runs a PR needs, recompute the human review card of
   * each named item that is at its human stage (it gains or loses Merge). Writes nothing for a card that is still right,
   * or for an item not at its human stage. Never creates anything.
   */
  RefreshReviewCards: { itemIds: Schema.Array(ItemId).check(Schema.isMaxLength(500)) },
  // ---- watchdog ----
  EscalateStall: {
    proposalId: ProposalId,
    subject: EntityRef,
    signal: Schema.String,
    options: Schema.Array(QuestionOption),
  },
  /** Wave A: a wake that would be the 4th agent-caused one in a row becomes (or joins) a card for the agent's owner. */
  HoldWake: {
    proposalId: ProposalId,
    agentId: AgentId,
    reason: WakeReason,
    cause: EventRef,
    from: Schema.NullOr(AgentId),
    /**
     * Wave C (decision 0071): which card. Absent = "chain" (Wave A's chain breaker, `chain:<agentId>`); "timed" = the
     * day's timed-turn cap (`timed:<agentId>:<day>`, escalation cause `timed_cap`). Optional: stored envelopes decode.
     */
    hold: opt(Schema.Literals(["chain", "timed"])),
  },
  // ---- wave C (decision 0071): monitors and the clock ----
  /**
   * A person sets a monitor (members, not viewers), or replaces one by its id (Edit). Decide stamps `ownerId` (the
   * setter), `state: active`, `createdByRoutine` and keeps `lastFiredAt`; only `trigger: cron` in wave C (at least one
   * hour apart). Accepting an agent's `monitor` card sends this as the person who decides.
   */
  SetMonitor: { monitor: Monitor },
  /** The setter, the agent's keeper or an admin. */
  RemoveMonitor: { monitorId: MonitorId },
  /** Pause or resume firing: the setter, the agent's keeper or an admin. */
  SetMonitorPaused: { monitorId: MonitorId, paused: Schema.Boolean },
  /** A blocked monitor: Resume as me. The person who resumes becomes the setter. Members, not viewers. */
  ResumeMonitor: { monitorId: MonitorId },
  /** system:scheduler: the clock reached the monitor's fire time `at`. Never creates anything. */
  FireMonitor: { monitorId: MonitorId, at: Millis },
  /**
   * system:scheduler, the daily check (A1): the loose-end keys open at `at` (core `looseEnds`). Sent only when the set
   * changed. Never creates anything.
   */
  NoteLooseEnds: { at: Millis, keys: Schema.Array(Schema.String).check(Schema.isMaxLength(500)) },
  /**
   * system:scheduler: a recurring series is due for `period` (yyyy-mm-dd) with no open occurrence, not paused or closed,
   * owned by an agent. Never creates the occurrence: the agent presses Run now (RunOccurrence). `at`: the schedule time
   * it came due (core `dueSeries`), so its timed turn counts against the same day as a monitor fire at that time; absent,
   * the time of the command.
   */
  NoteSeriesDue: { itemId: ItemId, period: Schema.String, at: Schema.optionalKey(Millis) },
  /**
   * Decision 0135: system:watchdog, a live run has produced nothing for `idleMs` (its heartbeats say so) while not
   * waiting on a permission. Wakes the run's agent (else the lead) once per quiet stretch; it decides to wait, steer or
   * stop. Never creates or stops anything.
   */
  NoteRunQuiet: { runId: RunId, idleMs: NonNegInt, at: Millis },
  // ---- sync and genesis ----
  SyncWorkspace: {
    /** Decision 0129: the project's roster (its members and the workspace owners/admins), with `projectRole`. */
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
    /** Decision 0129: who changed the project's members (the D1 write this sync mirrors); absent for plain syncs. */
    changedBy: Schema.optionalKey(UserId),
  },
  CreateProject: {
    projectId: ProjectId,
    workspaceId: WorkspaceId,
    slug: Slug,
    name: Schema.String,
    description: Markdown,
    repos: Schema.Array(RepoId),
    policyId: PolicyId,
    lead: Schema.Struct({
      agentId: AgentId,
      name: Schema.String,
      handle: Handle,
      mind: Mind,
      policyId: PolicyId,
    }),
    /** Decision 0129: absent = `workspace`. */
    visibility: Schema.optionalKey(Visibility),
  },
})

/** Every command the Project DO accepts. FlowCommand members are also valid as question-option effects. */
export const Command = Schema.Union([ProjectCommand, FlowCommand])
export type Command = typeof Command.Type
// #endregion commands

// #region results
export const CommandRejection = Schema.TaggedUnion({
  /** Schema or planner-validator failure. Messages are written so an agent can fix and retry. */
  Invalid: {
    issues: Schema.Array(
      Schema.Struct({ path: Schema.String, message: Schema.String, changeIndex: Schema.NullOr(Schema.Int) }),
    ),
  },
  Forbidden: {
    reason: Schema.Literals([
      "actor_kind",
      "not_owner",
      "policy_never",
      "held",
      "system_cannot_create",
      "self_approval",
      "frozen_contract",
      "autonomy", // decision 0048: the agent's level refuses this command in a turn that changes started
    ]),
    detail: Schema.String,
  },
  PreconditionFailed: { ref: EntityRef, expected: Seq, actual: Seq },
  /** Meaning-based dedupe hit (item intent, open proposal, run idempotency key). Points at the existing record. */
  Duplicate: { existing: EntityRef, reason: Schema.String },
  /** No longer produced (Wave A: WIP is a warning); kept so old clients and transcripts decode. */
  WipLimit: {
    scope: Schema.TaggedUnion({ area: { areaId: AreaId }, project: {} }),
    active: Schema.Int,
    limit: Schema.Int,
  },
  StaleLease: { runId: RunId, presented: Epoch, current: Epoch, ended: Schema.Boolean },
  StaleProposal: { proposalId: ProposalId, currentRevision: Schema.Int, state: ProposalState },
  NotFound: { ref: EntityRef },
  Conflict: { reason: Schema.String }, // e.g. idempotency key reused with a different body
})
export type CommandRejection = typeof CommandRejection.Type

const Applied = Schema.TaggedStruct("Applied", {
  firstSeq: Seq,
  lastSeq: Seq,
  planVersion: Schema.NullOr(Schema.Int),
})
const Proposed = Schema.TaggedStruct("Proposed", {
  proposalId: ProposalId,
  seq: Seq,
  /** apply_allowed: what was applied right away alongside the proposal. */
  applied: Schema.NullOr(Schema.Struct({ firstSeq: Seq, lastSeq: Seq, planVersion: Schema.Int })),
})
export const CommandResult = Schema.Union([
  Applied,
  Proposed,
  Schema.TaggedStruct("Deduplicated", { original: Schema.Union([Applied, Proposed]) }),
  Schema.TaggedStruct("Rejected", { rejection: CommandRejection }), // rejections write NO event
]).pipe(Schema.toTaggedUnion("_tag"))
export type CommandResult = typeof CommandResult.Type
// #endregion results
